<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <v-infinite-scroll @load="load">
                    <v-data-table-server
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="fehlerhafteRechnungenList"
                        item-value="tvsLieferantRechnungId"
                        :loading="loading"
                        hide-default-footer
                        class="elevation-1"
                        :items-length="totalNumberOfRecords || 0"
                        :must-sort="true"
                        :custom-sort="customSort"
                        hover
                        @update:sort-by="onSortByChanged"
                    >
                        <template #top>
                            <div>
                                <TableHeader :filters="filters" @chip-closed="removeFilter">
                                    <template #default>
                                        <div class="table-header-item-count">
                                            {{ totalNumberOfRecords + ' ' + $t('archiv.table.header.items') }}
                                        </div>
                                        <v-text-field
                                            v-if="showSearchBar"
                                            v-model="filterData.suchText"
                                            class="ml-5"
                                            prepend-inner-icon="mdi-magnify"
                                            append-inner-icon="mdi-close"
                                            :label="$t('tableheader.tooltip.search.rechnungen.fehlerhaft')"
                                            hide-details
                                            autofocus
                                            @update:model-value="searchFieldInputChanged"
                                            @click:append-inner="searchBarClosed"
                                        ></v-text-field>
                                        <v-spacer />
                                        <v-tooltip top color="secondary">
                                            <template #activator="{ props }">
                                                <v-btn v-bind="props" icon color="primary">
                                                    <v-icon v-if="!showSearchBar" @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                                </v-btn>
                                            </template>
                                            <div>
                                                <span>
                                                    {{ $t('tableheader.tooltip.search') }}
                                                </span>
                                            </div>
                                        </v-tooltip>
                                        <v-tooltip top color="secondary">
                                            <template #activator="{ props }">
                                                <v-btn v-bind="props" icon color="primary" @click="showFilterDrawer = !showFilterDrawer">
                                                    <v-icon>mdi-filter-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <div>
                                                <span>
                                                    {{ $t('tableheader.tooltip.filter') }}
                                                </span>
                                            </div>
                                        </v-tooltip>
                                    </template>
                                </TableHeader>
                            </div>
                        </template>
                        <template #item.lieferantName="{ item }">
                            <a class="text-primary cursor-pointer" style="text-decoration: underline" @click="initTakeSpecific(item.tvsLieferantTeilnehmerId)">
                                <TextHighlights :text="item.lieferantName" :search-text="filterData.suchText" />
                            </a>
                        </template>
                        <template #item.rechnungNummer="{ item }">
                            <TextHighlights :text="item.rechnungNummer" :search-text="filterData.suchText" />
                        </template>
                        <template #item.tvsKundennummer="{ item }">
                            <TextHighlights :text="item.tvsKundennummer" :search-text="filterData.suchText" />
                        </template>
                        <template #item.fehlermeldung="{ item }">
                            {{
                                item && item.fehlermeldung
                                    ? item.fehlermeldung
                                          .split(';')
                                          .map((error) => $t(error.trim(), error.trim()))
                                          .join('; ')
                                    : ''
                            }}
                        </template>
                        <template #item.burgermenu="{ item }">
                            <v-menu v-model="item.burgermenuActive" offset-x>
                                <template #activator="{ props }">
                                    <v-btn icon :href="item.burgermenu" v-bind="props" class="elevation-0">
                                        <v-icon color="primary" icon="mdi-menu" />
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item @click="retryRechnungProcessing(item.tvsLieferantRechnungId)">
                                        <template #prepend>
                                            <v-icon icon="mdi-reload" />
                                        </template>
                                        <v-list-item-title v-text="$t('rechnungen.fehlerhaft.burgermenu.retry')" />
                                    </v-list-item>

                                    <v-divider></v-divider>

                                    <v-list-item
                                        :disabled="!item.xmlDokumentGuid || item.xmlDokumentGuid === '00000000-0000-0000-0000-000000000000'"
                                        @click="
                                            downloadBeleg({
                                                id: item.xmlDokumentGuid,
                                                defaultFileName: `${item.xmlDokumentGuid}.xml`
                                            })
                                        "
                                    >
                                        <v-list-item-title v-text="$t('rechnungen.fehlerhaft.burgermenu.downloadxml')" />
                                    </v-list-item>
                                    <v-list-item @click="openXmlUploadDialog(item.tvsLieferantRechnungId)">
                                        <v-list-item-title v-text="$t('rechnungen.fehlerhaft.burgermenu.uploadxml')" />
                                    </v-list-item>
                                    <v-divider></v-divider>
                                    <v-list-item
                                        :disabled="!item.pdfDokumentGuid || item.pdfDokumentGuid === '00000000-0000-0000-0000-000000000000'"
                                        @click="
                                            item.pdfDokumentGuid &&
                                                openDokument({
                                                    id: item.pdfDokumentGuid,
                                                    dateiTyp: 'application/pdf',
                                                    dokumentName: `${item.pdfDokumentGuid}.pdf`
                                                })
                                        "
                                    >
                                        <v-list-item-title v-text="$t('rechnungen.fehlerhaft.burgermenu.viewpdf')" />
                                    </v-list-item>
                                    <v-list-item @click="openPdfUploadDialog(item.tvsLieferantRechnungId)">
                                        <v-list-item-title v-text="$t('rechnungen.fehlerhaft.burgermenu.uploadpdf')" />
                                    </v-list-item>

                                    <v-divider></v-divider>

                                    <!-- Rechnung löschen -->
                                    <ConfirmDialog
                                        title="rechnungen.fehlerhaft.deletedialog.title"
                                        text="rechnungen.fehlerhaft.deletedialog.text"
                                        @confirm="deleteFehlerhafteRechnung(item.tvsLieferantRechnungId)"
                                    >
                                        <template #activatorElement="{ props }">
                                            <v-list-item v-bind="props">
                                                <template #prepend>
                                                    <v-icon icon="mdi-trash-can" />
                                                </template>
                                                <v-list-item-title v-text="$t('archiv.table.burgermenu.delete')" />
                                            </v-list-item>
                                        </template>
                                    </ConfirmDialog>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-col>
        </v-row>
        <UploadDialog ref="xmlUploadDialog" file-type="XML" :on-upload="uploadFehlerhafteRechnungDatei" />
        <UploadDialog ref="pdfUploadDialog" file-type="PDF" :on-upload="uploadFehlerhafteRechnungDatei" />
        <FilterDrawer
            v-model:visible="showFilterDrawer"
            @cancel="cancelFilter()"
            @confirm="confirmFilter()"
            @clear="clearAllFilters(true)"
            @reset="clearAllFilters(false)"
        >
            <SingleFilter :title="$t('rechnungen.filter.header.lieferant')">
                <template #default>
                    <v-row>
                        <v-col sm="12">
                            <v-select
                                v-model="filterData.lieferantTeilnehmerIdForFilter"
                                variant="filled"
                                density="compact"
                                hide-details
                                :items="lieferanten"
                            ></v-select>
                        </v-col>
                    </v-row>
                </template>
            </SingleFilter>
            <SingleFilter title="">
                <template #default>
                    <div>
                        <div>
                            <v-radio-group v-model="radios" class="mt-0" inline>
                                <h5 class="pb-0">{{ $t('archiv.filter.title.datum') }}</h5>
                                <v-col cols="12">
                                    <v-radio value="specificTime" :label="$t('rechnungen.filter.label.specifictime')"></v-radio>
                                </v-col>
                                <v-col cols="12" class="pt-0">
                                    <v-row>
                                        <v-col cols="6">
                                            <NowhowDatepicker
                                                v-model="filterData.rechnungDatumVon"
                                                :disabled="radios !== 'specificTime'"
                                                :label="$t('archiv.filter.datum.von')"
                                                @change="onRechnungsDatumVonChange"
                                            >
                                            </NowhowDatepicker>
                                        </v-col>
                                        <v-col cols="6">
                                            <NowhowDatepicker
                                                v-model="filterData.rechnungDatumBis"
                                                :disabled="radios !== 'specificTime'"
                                                :label="$t('archiv.filter.datum.bis')"
                                                @change="onRechnungsDatumBisChange"
                                            >
                                            </NowhowDatepicker>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col cols="12" class="pt-0">
                                    <v-radio value="selectTime" :label="$t('rechnungen.filter.label.selecttime')"></v-radio>
                                </v-col>
                                <v-col cols="12" class="pt-0 pb-8">
                                    <v-select
                                        v-model="filterData.zeitspanne"
                                        :disabled="radios !== 'selectTime'"
                                        variant="filled"
                                        density="compact"
                                        hide-details
                                        :items="ddTimeItems"
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" class="pt-0 pb-0">
                                    <v-radio value="allTime" :label="$t('kontobrowser.filter.alltime')"></v-radio>
                                </v-col>
                            </v-radio-group>
                        </div>
                    </div>
                </template>
            </SingleFilter>
        </FilterDrawer>
    </v-container>
</template>

<script lang="ts">
import { Component, Watch, mixins } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import { DateMixin } from '@/mixins/DateMixin';
import { ZeitspanneFilterApiDto, TvsLieferantFehlerhafteRechnungApiDto, TakeTeilnehmerApiDto } from '@/api-schema';
import { TakeTeilnehmerDto } from '@/types/UserDtos';
import UploadDialog from '@/components/UploadDialog.vue';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
const generalModul = namespace(NAME_GENERAL);
import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';

import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
const headerNotificationModul = namespace(NAME_HEADERNOTIFICATION);
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';

import { NAME as NAME_SECURITY } from '@/store/modules/security';
const securityModul = namespace(NAME_SECURITY);
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';

import { NAME as NAME_ADMINISTRATION } from '../store';
const administrationModul = namespace(NAME_ADMINISTRATION);
import {
    GET_ALL_FEHLERHAFTE_RECHNUNGEN_ADMIN,
    DELETE_FEHLERHAFTE_RECHNUNG_ADMIN,
    RETRY_RECHNUNG_PROCESSING_ADMIN,
    UPLOAD_FEHLERHAFTE_RECHNUNG_DATEI_ADMIN,
    DOWNLOAD_BELEG_ADMIN,
    OPEN_DOKUMENT_ADMIN,
    LOAD_LIEFERANTEN,
    TAKESPECIFIC
} from '../store/actions';
import { APPLY_FEHLERHAFTE_RECHNUNGEN_FILTER_ADMIN, CLEAR_FEHLERHAFTE_RECHNUNGEN_FILTER_ADMIN } from '../store/mutations';
import { FEHLERHAFTE_RECHNUNGEN_ADMIN, FEHLERHAFTE_RECHNUNGEN_LOADING_ADMIN, FEHLERHAFTE_RECHNUNGEN_TOTAL_ADMIN, GET_LIEFERANTEN } from '../store/getters';

import { NAME as NAME_RESOURCE } from '@/store/modules/resource';
const resourceModul = namespace(NAME_RESOURCE);
import { GET_LANGUAGE } from '@/store/modules/resource/getters';

import { RechnungFilterDto } from '@/types/RechnungFilterDto';

import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TableHeader from '@/components/TableHeader.vue';
import NowhowDatepicker from '@/components/NowhowDatepicker.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import debounce from 'lodash/debounce';
import { TableHeaderFilterItem } from '@/types/TableHeaderFilterItem';
import ConfirmDialog from '@/components/ConfirmDialog.vue';

@Component({
    components: {
        ConfirmDialog,
        SingleFilter,
        FilterDrawer,
        TableHeader,
        NowhowDatepicker,
        TextHighlights,
        UploadDialog
    }
})
export default class FehlerhafteRechnungen extends mixins(DateMixin) {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @headerNotificationModul.Action(NEW_ALERT) alertMessage!: Function;

    @administrationModul.Action(GET_ALL_FEHLERHAFTE_RECHNUNGEN_ADMIN) getAll!: Function;
    @administrationModul.Action(DELETE_FEHLERHAFTE_RECHNUNG_ADMIN) deleteFehlerhafteRechnung!: Function;
    @administrationModul.Action(RETRY_RECHNUNG_PROCESSING_ADMIN) retryRechnungProcessing!: Function;
    @administrationModul.Action(UPLOAD_FEHLERHAFTE_RECHNUNG_DATEI_ADMIN) uploadFehlerhafteRechnungDatei!: Function;
    @administrationModul.Action(DOWNLOAD_BELEG_ADMIN) downloadBeleg!: Function;
    @administrationModul.Action(OPEN_DOKUMENT_ADMIN) openDokument!: Function;
    @administrationModul.Action(LOAD_LIEFERANTEN) loadLieferanten!: Function;
    @administrationModul.Action(TAKESPECIFIC) takeSpecific!: Function;

    @administrationModul.Getter(GET_LIEFERANTEN) lieferanten!: { title: string; value: string }[];
    @administrationModul.Getter(FEHLERHAFTE_RECHNUNGEN_ADMIN)
    fehlerhafteRechnungenList!: Array<TvsLieferantFehlerhafteRechnungApiDto>;
    @administrationModul.Getter(FEHLERHAFTE_RECHNUNGEN_LOADING_ADMIN) loading!: boolean;
    @administrationModul.Getter(FEHLERHAFTE_RECHNUNGEN_TOTAL_ADMIN) totalNumberOfRecords!: number;
    @administrationModul.Mutation(CLEAR_FEHLERHAFTE_RECHNUNGEN_FILTER_ADMIN) clearFilters!: Function;
    @administrationModul.Mutation(APPLY_FEHLERHAFTE_RECHNUNGEN_FILTER_ADMIN) applyFilters!: Function;
    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;
    @resourceModul.Getter(GET_LANGUAGE) language!: string;

    file = null;
    showSearchBar = false;
    searchField = '';
    filters: Array<TableHeaderFilterItem> = [];
    title = '';
    filterData: RechnungFilterDto = new RechnungFilterDto();
    filterDataCopy: RechnungFilterDto = new RechnungFilterDto();
    showFilterDrawer = false;
    radios = 'selectTime';
    radiosCopy = '';
    sortBy: [{ key: 'rechnungsanlieferungDatum'; order: 'desc' }];
    infiniteLoad = null;

    get ddTimeItems(): Array<object> {
        return [
            {
                title: this.$t('kontobrowser.filter.timeitems.letztezeit'),
                value: 'Last60Days' as ZeitspanneFilterApiDto
            },
            {
                title: this.$t('kontobrowser.filter.timeitems.letzte180tage'),
                value: 'Last180Days' as ZeitspanneFilterApiDto
            },
            {
                title: this.$t('kontobrowser.filter.timeitems.aktuelljahr'),
                value: 'CurrentYear' as ZeitspanneFilterApiDto
            },
            {
                title: this.$t('kontobrowser.filter.timeitems.vorjahr'),
                value: 'LastYear' as ZeitspanneFilterApiDto
            }
        ];
    }

    get headers(): Array<any> {
        return [
            {
                title: this.$t('rechnungen.fehlerhaft.table.header.lieferantname'),
                key: 'lieferantName'
            },
            {
                title: this.$t('rechnungen.fehlerhaft.table.header.rechnungnummer'),
                key: 'rechnungNummer'
            },
            {
                title: this.$t('rechnungen.fehlerhaft.table.header.datum'),
                key: 'rechnungDatum'
            },
            {
                title: this.$t('rechnungen.fehlerhaft.table.header.tvskundennummer'),
                key: 'tvsKundennummer',
                sortable: false
            },
            {
                title: this.$t('rechnungen.fehlerhaft.table.header.rechnungsanlieferungdatum'),
                key: 'rechnungsanlieferungDatum'
            },
            {
                title: this.$t('rechnungen.fehlerhaft.table.header.fehlermeldung'),
                key: 'fehlermeldung',
                sortable: false
            },
            {
                title: '',
                value: 'burgermenu',
                align: 'center',
                width: '10',
                sortable: false
            }
        ];
    }

    created() {
        this.setSubTitle(this.$t('rechnung.fehlerhaft.subtitle'));
        this.setFooterTitle('S950');
    }

    mounted() {
        if (!this.hasAccess('Navigation.FehlerhafteRechnungen')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
            this.filterData.zeitspanne = 'Last180Days';
            this.filterData.sortBy = 'RechnungsanlieferungDatum';
            this.filterDataCopy.sortBy = 'RechnungsanlieferungDatum';
            this.filters.push({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: this.$t('rechnungen.filter.chip.value.last180').toString()
            });
            this.applyFilters(this.filterData);
            this.loadLieferanten();
            this.getAll(0);
        }
        this.searchFieldInputChanged = debounce(() => {
            this.applyFilters(this.filterData);
            this.getAll(0);
        }, 400);
    }

    searchFieldInputChanged!: () => void;

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.suchText = undefined;
        this.applyFilters(this.filterData);
        this.getAll(0);
    }

    private ShowActiveFilters() {
        this.filters = [];
        if (this.radios === 'allTime') {
            this.filterData.rechnungDatumVon = undefined;
            this.filterData.rechnungDatumBis = undefined;
            this.filterData.zeitspanne = undefined;
        }
        if (this.radios === 'specificTime') {
            let filterValue = '';
            if (this.filterData.rechnungDatumVon && this.filterData.rechnungDatumBis) {
                filterValue = this.filterData.rechnungDatumVon + ' - ' + this.filterData.rechnungDatumBis;
            } else if (this.filterData.rechnungDatumVon) {
                filterValue = '<= ' + this.filterData.rechnungDatumVon;
            } else if (this.filterData.rechnungDatumBis) {
                filterValue = '>= ' + this.filterData.rechnungDatumBis;
            }

            this.filters = this.filters.concat({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: filterValue
            });
        }
        if (this.radios === 'selectTime') {
            this.filterData.rechnungDatumVon = undefined;
            this.filterData.rechnungDatumBis = undefined;
            if (this.filterData.zeitspanne === 'Last60Days') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.recent').toString()
                });
            } else if (this.filterData.zeitspanne === 'Last180Days') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.last180').toString()
                });
            } else if (this.filterData.zeitspanne === 'CurrentYear') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.currentyear').toString()
                });
            } else if (this.filterData.zeitspanne === 'LastYear') {
                this.filters = this.filters.concat({
                    key: 'archiv.filter.chip.title.datum',
                    nameResourceKey: 'archiv.filter.chip.title.datum',
                    value: this.$t('rechnungen.filter.chip.value.lastyear').toString()
                });
            }
            if (this.filterData.lieferantTeilnehmerIdForFilter) {
                const lieferant = this.lieferanten.find((lieferant) => lieferant.value === this.filterData.lieferantTeilnehmerIdForFilter);
                if (lieferant?.title) {
                    this.filters = this.filters.concat({
                        key: 'rechnungen.fehlerhaft.table.header.lieferantname',
                        nameResourceKey: 'rechnungen.fehlerhaft.table.header.lieferantname',
                        value: lieferant.title
                    });
                }
            }
        }
    }

    private customSort(items: any[]) {
        return items;
    }

    private removeFilter(value: string) {
        console.log('removeFilter: ' + value + 'removed');
        this.filters = this.filters.filter(function (obj) {
            return obj.key !== value;
        });
        if (value === 'archiv.filter.chip.title.datum') {
            this.filterData.rechnungDatumBis = undefined;
            this.filterData.rechnungDatumVon = undefined;
            this.filterData.zeitspanne = undefined;
            this.radios = 'allTime';
        } else if (value === 'rechnungen.fehlerhaft.table.header.lieferantname') {
            this.filterData.lieferantTeilnehmerIdForFilter = undefined;
        }
        this.applyFilters(this.filterData);
        this.getAll(0);
    }

    private clearAllFilters(closeFilter: boolean) {
        this.radios = 'selectTime';
        this.filterData = new RechnungFilterDto();
        this.filterData.rechnungDatumBis = undefined;
        this.filterData.rechnungDatumVon = undefined;
        this.filterData.zeitspanne = 'Last180Days';
        if (closeFilter) {
            this.clearFilters();
            this.filters = [];
            this.applyFilters(this.filterData);
            this.showFilterDrawer = false;
        }
    }

    async load({ done }) {
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            done('ok');
            return;
        }
        await this.getAll(this.fehlerhafteRechnungenList.length);
        if (this.fehlerhafteRechnungenList.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }

    private confirmFilter() {
        this.ShowActiveFilters();
        this.applyFilters(this.filterData);
        this.getAll(0);
        this.showFilterDrawer = false;
    }

    private cancelFilter() {
        this.filterData = Object.assign({}, this.filterDataCopy);
        this.radios = this.radiosCopy;
        this.showFilterDrawer = false;
    }

    @Watch('showFilterDrawer')
    onFilterDrawerOpened() {
        if (this.showFilterDrawer) {
            this.filterDataCopy = Object.assign({}, this.filterData);
            this.radiosCopy = this.radios;
        }
    }

    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0];
            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }
        this.applyFilters(this.filterData);
        this.getAll(0);
    }

    @Watch('language')
    onLanguageChanged() {
        this.ShowActiveFilters();
    }

    onRechnungsDatumVonChange(newValue: string) {
        if (newValue) {
            this.filterData.rechnungDatumVon = newValue;
        }
    }

    onRechnungsDatumBisChange(newValue: string) {
        if (newValue) {
            this.filterData.rechnungDatumBis = newValue;
        }
    }

    private openXmlUploadDialog(tvsLieferantRechnungId: string) {
        const dialog = this.$refs.xmlUploadDialog as any;
        if (dialog) {
            dialog.openDialog(tvsLieferantRechnungId);
        }
    }

    private openPdfUploadDialog(tvsLieferantRechnungId: string) {
        const dialog = this.$refs.pdfUploadDialog as any;
        if (dialog) {
            dialog.openDialog(tvsLieferantRechnungId);
        }
    }

    private initTakeSpecific(teilnehmerId: number) {
        // Rolle vom Teilnehmer im Backend ermitteln. Deswegen geben wir hier NULL
        const teilnehmer: TakeTeilnehmerApiDto = new TakeTeilnehmerDto(null, teilnehmerId);
        this.takeSpecific(teilnehmer);
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
